import { useLingui } from "@lingui/react/macro";
import { useEffect, useState } from "react";
import { rpc } from "../../lib/rpc";

type UsageRecord = Awaited<ReturnType<typeof rpc.usage.list>>[number];

function compactTokens(value: number) {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}k`;
  return String(value);
}

/**
 * Kommerce: what this bot has cost today, from the usage records the server already keeps.
 * Refreshes when a run finishes.
 */
export function ComposerUsage({ botId, running }: { botId: string; running: boolean }) {
  const { t } = useLingui();
  const [records, setRecords] = useState<UsageRecord[] | null>(null);

  useEffect(() => {
    if (running) return;
    let live = true;
    rpc.usage
      .list()
      .then((next) => live && setRecords(next))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [botId, running]);

  if (!records) return null;
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const today = records.filter(
    (record) => record.botId === botId && Date.parse(record.createdAt) >= startOfDay.getTime(),
  );
  const cost = today.reduce((sum, record) => sum + (record.costUsd ?? 0), 0);
  const tokens = today.reduce(
    (sum, record) =>
      sum + (record.totalTokens ?? (record.inputTokens ?? 0) + (record.outputTokens ?? 0)),
    0,
  );
  const label = cost > 0 ? `$${cost < 0.01 ? cost.toFixed(3) : cost.toFixed(2)}` : compactTokens(tokens);

  return (
    <span
      data-testid="composer-usage"
      title={t`Today with this bot: ${compactTokens(tokens)} tokens across ${today.length} model calls`}
      className="inline-flex h-8 shrink-0 items-center px-2 text-xs tabular-nums text-muted-foreground"
    >
      {label}
    </span>
  );
}
